import { useState, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui-system";
import {
  apiErrorMessage, useTemplateRecipients, RecipientsCard, EmailPreviewHeader,
} from "@/components/EmailTemplateRecipients";
import { toast } from "sonner";
import {
  Plus, Trash2, Send, Loader2, Settings, ListChecks, Hash, Paperclip, Image as ImageIcon, FileText,
} from "lucide-react";

/**
 * Lost and Damage Email Notification — reports one or more LRNs as damaged or lost.
 * EACH LRN carries its own proof images and ticket; all are ATTACHED to the one
 * email, file names prefixed with their LRN.
 *
 * Recipients: the saved notification_configs doc for LOST_DAMAGE_ACTION_TYPE (shared
 * Recipients card). Trigger Now posts multipart to
 * POST /notification-config/admin/lost-damage/send:
 *   issue_type, lrns (repeated, in order), proof_images_<i>, ticket_<i>
 * The backend re-validates every file (type by content, size, counts, total) and
 * attaches them in memory — never stored (backend/services/lost_damage_service.py).
 *
 * The preview wording mirrors render() in that service — change both together.
 */
export const LOST_DAMAGE_ACTION_TYPE = "lost_damage";

// Mirrors backend/models/lost_damage.py — the backend re-validates.
const LRN_RE = /^[A-Za-z0-9-]{1,40}$/;
const MAX_LRNS = 10;
const MAX_PROOF_IMAGES = 10; // per LRN
const MB = 1024 * 1024;
const MAX_FILE_BYTES = 5 * MB;
const MAX_TOTAL_BYTES = 15 * MB; // across ALL LRNs in the email
const PROOF_EXTS = ["jpg", "jpeg", "png", "pdf"];
const TICKET_EXTS = [...PROOF_EXTS, "doc", "docx", "txt"];

// [singular, plural] — picked by how many LRNs the email reports.
const ISSUES = {
  damage: {
    label: "Damage",
    subject: ["Damage Email Notification – LRN", "Damage Email Notification – LRNs"],
    intro: ["The below LRN was received damaged.", "The below LRNs were received damaged."],
  },
  lost: {
    label: "Lost",
    subject: ["Lost Email Notification – LRN", "Lost Email Notification – LRNs"],
    intro: ["The below LRN has been lost.", "The below LRNs have been lost."],
  },
};
const INTRO_SUFFIX = " PFA images and ticket for reference. Kindly provide clarification at the earliest.";

let seq = 0;
const uid = () => `ld-${++seq}`;
const emptyEntry = () => ({ key: uid(), lrn: "", proofs: [], ticket: null });
const ext = (name) => (name.split(".").pop() || "").toLowerCase();
const fmtSize = (bytes) => (bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const entryFiles = (en) => [...en.proofs, ...(en.ticket ? [en.ticket] : [])];

// Mirrors safe_filename / attachment_name in the service, so the preview shows the
// names the recipient will actually see.
function safeName(name) {
  const base = (name || "").split(/[\\/]/).pop();
  const dot = base.lastIndexOf(".");
  const [stemRaw, extRaw] = dot > 0 ? [base.slice(0, dot), base.slice(dot)] : [base, ""];
  const stem = stemRaw.replace(/[^A-Za-z0-9._ ()-]/g, "_").replace(/^[ .]+|[ .]+$/g, "") || "file";
  const extension = extRaw.replace(/[^A-Za-z0-9.]/g, "").slice(0, 10);
  return `${stem.slice(0, 100 - extension.length)}${extension}`;
}
function attachmentNames(entries) {
  const taken = new Set();
  return entries.map((en) => entryFiles(en).map(({ file }) => {
    let name = safeName(`${en.lrn.trim()}_${safeName(file.name)}`);
    const dot = name.lastIndexOf(".");
    const [stem, extension] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ""];
    for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${stem} (${n})${extension}`;
    taken.add(name.toLowerCase());
    return name;
  }));
}

/** Why `file` cannot be attached (null = it can). */
function fileError(file, allowedExts) {
  if (!allowedExts.includes(ext(file.name))) return `type not allowed (${allowedExts.join(", ").toUpperCase()})`;
  if (file.size === 0) return "file is empty";
  if (file.size > MAX_FILE_BYTES) return `larger than ${MAX_FILE_BYTES / MB} MB`;
  return null;
}

/** { errors: { [entryKey]: { lrn, proof, ticket } }, count } */
function validateEntries(entries) {
  const counts = {};
  entries.forEach((en) => {
    const k = en.lrn.trim().toUpperCase();
    if (k) counts[k] = (counts[k] || 0) + 1;
  });
  const errors = {};
  let count = 0;
  entries.forEach((en) => {
    const lrn = en.lrn.trim();
    const e = {
      lrn: !lrn ? "LRN is required"
        : !LRN_RE.test(lrn) ? "Letters, digits and hyphens only (max 40)"
          : counts[lrn.toUpperCase()] > 1 ? "Duplicate LRN — each LRN can appear once" : null,
      proof: en.proofs.length ? null : "Attach at least one proof image",
      ticket: en.ticket ? null : "Attach the ticket / reference document",
    };
    count += Object.values(e).filter(Boolean).length;
    errors[en.key] = e;
  });
  return { errors, count };
}

function UploadTile({ icon: Icon, title, hint, buttonLabel, accept, multiple, onFiles, disabled, testId }) {
  const inputRef = useRef(null);
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 flex flex-col items-center text-center gap-2">
      <div className="flex items-center gap-3 self-stretch">
        <div className="p-2 rounded-lg bg-teal-100 text-teal-700 shrink-0"><Icon className="w-5 h-5" /></div>
        <div className="text-left min-w-0">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{hint}</p>
        </div>
      </div>
      <input ref={inputRef} type="file" className="hidden" accept={accept} multiple={multiple}
        onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
        data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={disabled}
        onClick={() => inputRef.current?.click()} data-testid={testId}>
        <Plus className="w-3.5 h-3.5 mr-1" />{buttonLabel}
      </Button>
    </div>
  );
}

function LrnEntry({ index, entry, errors, showAll, canRemove, onChange, onRemove, onAddFiles, onRemoveFile, attachError }) {
  const lrnErr = (showAll || entry.lrn.trim()) ? errors?.lrn : null;
  const proofErr = showAll ? errors?.proof : null;
  const ticketErr = showAll ? errors?.ticket : null;
  const files = entryFiles(entry);
  const lrnId = `${entry.key}-lrn`;

  return (
    <div className="rounded-lg border border-gray-200 bg-slate-50/60 p-4 space-y-3" data-testid={`ld-entry-${index}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <label htmlFor={lrnId} className="text-xs font-medium text-gray-500 uppercase tracking-wider">LRN #{index + 1}</label>
          <Input id={lrnId} value={entry.lrn} maxLength={40} placeholder="Enter LRN (e.g. 312604074)"
            aria-invalid={Boolean(lrnErr)} className={`mt-1 bg-white ${lrnErr ? "border-red-500" : ""}`}
            onChange={(e) => onChange({ ...entry, lrn: e.target.value })} data-testid={`ld-lrn-input-${index}`} />
          {lrnErr && <p className="text-xs text-red-600 mt-1">{lrnErr}</p>}
        </div>
        <Button type="button" variant="ghost" size="icon" className="mt-6 shrink-0 text-red-500 hover:text-red-700"
          disabled={!canRemove} aria-label={`Delete LRN #${index + 1}`}
          title={canRemove ? "Delete this LRN and its files" : "At least one LRN is required"}
          onClick={onRemove} data-testid={`ld-remove-lrn-${index}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <UploadTile icon={ImageIcon} title="Proof Images" hint={`JPG, PNG or PDF · up to ${MAX_PROOF_IMAGES}`}
            buttonLabel="Upload Files" accept=".jpg,.jpeg,.png,.pdf" multiple
            disabled={entry.proofs.length >= MAX_PROOF_IMAGES}
            onFiles={(fs) => onAddFiles(fs, "proof")} testId={`ld-upload-proof-${index}`} />
          {proofErr && <p className="text-xs text-red-600 mt-1">{proofErr}</p>}
        </div>
        <div>
          <UploadTile icon={FileText} title="Ticket / Reference Document" hint="PDF, image, DOC/DOCX or TXT · 1 file"
            buttonLabel={entry.ticket ? "Replace File" : "Upload File"} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
            onFiles={(fs) => onAddFiles(fs.slice(0, 1), "ticket")} testId={`ld-upload-ticket-${index}`} />
          {ticketErr && <p className="text-xs text-red-600 mt-1">{ticketErr}</p>}
        </div>
      </div>
      {attachError && <p className="text-xs text-red-600" role="alert">{attachError}</p>}

      {files.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white" data-testid={`ld-file-list-${index}`}>
          {files.map(({ key, file }) => {
            const isTicket = entry.ticket?.key === key;
            const Icon = isTicket ? FileText : ImageIcon;
            return (
              <li key={key} className="flex items-center gap-3 px-3 py-2">
                <Icon className="w-4 h-4 text-teal-600 shrink-0" />
                <span className="text-sm text-gray-800 break-all min-w-0 flex-1">
                  {file.name} <span className="text-gray-400">({fmtSize(file.size)})</span>
                  {isTicket && <span className="ml-1 text-xs text-gray-500">· Ticket</span>}
                </span>
                <Button type="button" variant="ghost" size="icon" className="shrink-0 text-red-500 hover:text-red-700"
                  aria-label={`Remove ${file.name}`} onClick={() => onRemoveFile(key)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmailPreview({ sender, to, cc, bcc, issueType, entries }) {
  const issue = ISSUES[issueType];
  const many = entries.length > 1 ? 1 : 0;
  const names = attachmentNames(entries);
  return (
    <div className="text-sm">
      <EmailPreviewHeader sender={sender} to={to} cc={cc} bcc={bcc}
        subject={issue ? issue.subject[many] : "— (select Damage or Lost)"} subjectTestId="ld-preview-subject" />
      <div className="pt-3 space-y-3 text-gray-800" data-testid="ld-preview-body">
        <p>Hi Delhivery Team,</p>
        {issue ? <p>{issue.intro[many]}{INTRO_SUFFIX}</p> : <p className="text-gray-400">Select Damage or Lost to see the message.</p>}
        <div className="rounded-lg bg-slate-100 px-4 py-3">
          <p className="font-medium">{many ? "LRNs:" : "LRN:"}</p>
          <ul className="list-disc pl-5 space-y-1">
            {entries.map((en, i) => (
              <li key={en.key} className="break-all">
                {en.lrn.trim() || "—"}
                <span className="block text-xs text-gray-500">
                  Attached: {names[i].length ? names[i].join(", ") : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <p>Thanks &amp; Regards,<br />{sender?.from_name || "EdgeOS Support Team"}</p>
      </div>
    </div>
  );
}

export default function LostDamagePanel({ user, configs, loading, onConfigSaved }) {
  const recipients = useTemplateRecipients({
    actionType: LOST_DAMAGE_ACTION_TYPE, senderPath: "lost-damage/sender",
    user, configs, onConfigSaved,
  });
  const { to, cc, bcc, sender, sendBlocker } = recipients;

  const [issueType, setIssueType] = useState("");
  const [entries, setEntries] = useState(() => [emptyEntry()]);
  const [attachErrors, setAttachErrors] = useState({}); // { [entryKey]: message }
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const allFiles = entries.flatMap(entryFiles);
  const totalBytes = allFiles.reduce((sum, f) => sum + f.file.size, 0);
  const { errors, count: entryErrorCount } = validateEntries(entries);
  const errorCount = entryErrorCount + (issueType ? 0 : 1);

  const updateEntry = (key, fn) => setEntries((prev) => prev.map((en) => (en.key === key ? fn(en) : en)));

  /** Accept what fits; report every rejected file and why (never silently dropped). */
  const addFiles = (entry, incoming, kind) => {
    const allowed = kind === "proof" ? PROOF_EXTS : TICKET_EXTS;
    const rejected = [];
    const accepted = [];
    // A replaced ticket's bytes no longer count toward the email total.
    let running = totalBytes - (kind === "ticket" ? entry.ticket?.file.size || 0 : 0);
    let slots = kind === "proof" ? MAX_PROOF_IMAGES - entry.proofs.length : 1;
    incoming.forEach((file) => {
      const err = fileError(file, allowed);
      if (err) { rejected.push(`${file.name}: ${err}`); return; }
      if (slots <= 0) { rejected.push(`${file.name}: at most ${MAX_PROOF_IMAGES} proof images per LRN`); return; }
      if (running + file.size > MAX_TOTAL_BYTES) {
        rejected.push(`${file.name}: attachments would exceed ${MAX_TOTAL_BYTES / MB} MB for this email`);
        return;
      }
      running += file.size;
      slots -= 1;
      accepted.push({ key: uid(), file });
    });
    if (accepted.length) {
      updateEntry(entry.key, (en) => (kind === "proof"
        ? { ...en, proofs: [...en.proofs, ...accepted] }
        : { ...en, ticket: accepted[0] }));
    }
    const msg = rejected.length ? `Not attached — ${rejected.join("; ")}` : null;
    setAttachErrors((prev) => ({ ...prev, [entry.key]: msg }));
    if (msg) toast.error(msg);
  };

  const removeFile = (entryKey, fileKey) => {
    updateEntry(entryKey, (en) => ({
      ...en,
      proofs: en.proofs.filter((f) => f.key !== fileKey),
      ticket: en.ticket?.key === fileKey ? null : en.ticket,
    }));
    setAttachErrors((prev) => ({ ...prev, [entryKey]: null }));
  };

  const requestSend = () => {
    setShowAllErrors(true);
    if (sendBlocker) { toast.error(sendBlocker); return; }
    if (errorCount) { toast.error(`Fix ${errorCount} highlighted field${errorCount === 1 ? "" : "s"} before sending`); return; }
    setConfirmOpen(true);
  };

  const send = async () => {
    if (sending) return;
    setSending(true);
    const form = new FormData();
    form.append("issue_type", issueType);
    entries.forEach((en, i) => {
      form.append("lrns", en.lrn.trim());
      en.proofs.forEach(({ file }) => form.append(`proof_images_${i}`, file, file.name));
      form.append(`ticket_${i}`, en.ticket.file, en.ticket.file.name);
    });
    try {
      const res = await axios.post(`${API}/notification-config/admin/lost-damage/send`, form);
      if (res.data?.success) {
        toast.success(res.data.message || "Email sent");
        // Cleared so a second click cannot resend the same report by accident.
        setIssueType(""); setEntries([emptyEntry()]); setAttachErrors({}); setShowAllErrors(false);
      } else {
        toast.error(res.data?.error || "The email could not be sent");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "The email could not be sent"));
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  if (loading && !configs.length) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" aria-busy="true">
        {[0, 1].map((i) => <div key={i} className="h-72 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  const issueErr = showAllErrors && !issueType ? "Select Damage or Lost" : null;
  const many = entries.length > 1 ? 1 : 0;

  return (
    <div className="space-y-6" data-testid="lost-damage-panel">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-teal-600 shrink-0" />
          Lost and Damage Email Notification Template
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          This template is used to send notifications for lost or damaged shipments. It alerts stakeholders about
          the issue and requires clarification with supporting evidence.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <RecipientsCard recipients={recipients} idPrefix="ld"
            description="Send notifications to the users who need to be aware of lost or damaged shipments." />

          {/* Issue type — one of Damage / Lost for the whole email */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-gray-500" />Issue Type
              </CardTitle>
              <p className="text-xs text-gray-500">Select the issue type for this notification. It applies to every LRN below.</p>
            </CardHeader>
            <CardContent>
              <RadioGroup value={issueType} onValueChange={setIssueType} className="flex flex-wrap gap-6"
                aria-label="Issue type" data-testid="ld-issue-type">
                {Object.entries(ISSUES).map(([value, { label }]) => (
                  <div key={value} className="flex items-center gap-2">
                    <RadioGroupItem value={value} id={`ld-issue-${value}`} data-testid={`ld-issue-${value}`} />
                    <Label htmlFor={`ld-issue-${value}`} className="cursor-pointer">{label}</Label>
                  </div>
                ))}
              </RadioGroup>
              {issueErr && <p className="text-xs text-red-600 mt-2">{issueErr}</p>}
            </CardContent>
          </Card>

          {/* LRNs — each with its own proof images and ticket; sent with the email, never stored */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Hash className="w-5 h-5 text-gray-500" />LRN Details &amp; Attachments
                  <Badge variant="secondary" className="text-xs">{entries.length}</Badge>
                </CardTitle>
                <Button type="button" variant="outline" disabled={entries.length >= MAX_LRNS}
                  onClick={() => setEntries((prev) => [...prev, emptyEntry()])} data-testid="ld-add-lrn">
                  <Plus className="w-4 h-4 mr-1" />Add LRN
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Each LRN needs its own proof images and ticket. Files are attached to the email when you click Trigger
                Now, named with their LRN (max {MAX_FILE_BYTES / MB} MB per file, {MAX_TOTAL_BYTES / MB} MB in total,
                up to {MAX_LRNS} LRNs).
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {entries.map((en, i) => (
                <LrnEntry key={en.key} index={i} entry={en} errors={errors[en.key]} showAll={showAllErrors}
                  canRemove={entries.length > 1} attachError={attachErrors[en.key]}
                  onChange={(next) => updateEntry(en.key, () => next)}
                  onRemove={() => setEntries((prev) => prev.filter((x) => x.key !== en.key))}
                  onAddFiles={(fs, kind) => addFiles(en, fs, kind)}
                  onRemoveFile={(fileKey) => removeFile(en.key, fileKey)} />
              ))}
              <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                <Paperclip className="w-3.5 h-3.5" />
                {allFiles.length} file{allFiles.length === 1 ? "" : "s"} · {fmtSize(totalBytes)} of {MAX_TOTAL_BYTES / MB} MB
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Preview — sticky beside the form on wide screens, below it otherwise */}
        <Card className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-lg">Email Preview</CardTitle>
              <Button type="button" className="bg-teal-600 hover:bg-teal-700"
                onClick={requestSend} disabled={sending} data-testid="ld-trigger-now">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Trigger Now
              </Button>
            </div>
            {sendBlocker && <p className="text-xs text-amber-700">{sendBlocker}</p>}
            {!sendBlocker && showAllErrors && errorCount > 0 && (
              <p className="text-xs text-red-600">{errorCount} field{errorCount === 1 ? "" : "s"} need attention before sending.</p>
            )}
          </CardHeader>
          <CardContent>
            <EmailPreview sender={sender} to={to} cc={cc} bcc={bcc} issueType={issueType} entries={entries} />
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!sending) setConfirmOpen(o); }}
        title={`Send ${ISSUES[issueType]?.label || ""} email?`}
        description={`"${ISSUES[issueType]?.subject[many] || ""}" for ${entries.length} LRN(s) with ${allFiles.length} attachment(s) (${fmtSize(totalBytes)}) will be emailed to ${to.length} To, ${cc.length} CC and ${bcc.length} BCC recipient(s). This sends a real email and cannot be undone.`}
        confirmLabel="Send Email"
        loading={sending}
        onConfirm={send}
      />
    </div>
  );
}
